import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useReviews } from "@/hooks/useReviews";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";

interface ReviewsSectionProps {
  productId: number;
  productName: string;
}

const STAR_LABELS = ["", "سيء", "مقبول", "جيد", "جيد جداً", "ممتاز"];
const STAR_COLORS = ["", "text-red-400", "text-orange-400", "text-yellow-400", "text-lime-400", "text-emerald-400"];

/* ── Star Rating ── */
function StarRating({
  value, onChange, size = "lg",
}: {
  value: number; onChange?: (v: number) => void; size?: "sm" | "md" | "lg";
}) {
  const [hovered, setHovered] = useState(0);
  const active = hovered || value;
  const sizeClass = size === "lg" ? "text-3xl" : size === "md" ? "text-xl" : "text-sm";

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(s => (
        <button
          key={s}
          type="button"
          onClick={() => onChange?.(s)}
          onMouseEnter={() => onChange && setHovered(s)}
          onMouseLeave={() => onChange && setHovered(0)}
          className={`${sizeClass} transition-all duration-150 ${onChange ? "cursor-pointer hover:scale-110" : "cursor-default"} ${
            s <= active ? "text-amber-400" : "text-gray-700"
          }`}
        >
          <i className="ri-star-fill"></i>
        </button>
      ))}
    </div>
  );
}

/* ── Review Card ── */
function ReviewCard({ review }: { review: ReturnType<typeof useReviews>["reviews"][0] }) {
  const [helpful, setHelpful] = useState(0);
  const [voted, setVoted] = useState(false);

  const date = new Date(review.createdAt).toLocaleDateString("ar-SA", {
    year: "numeric", month: "long", day: "numeric",
  });

  const handleHelpful = (val: number) => {
    if (voted) return;
    setHelpful(h => h + val);
    setVoted(true);
  };

  const ratingColors: Record<number, string> = {
    5: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
    4: "text-lime-400 bg-lime-400/10 border-lime-400/20",
    3: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
    2: "text-orange-400 bg-orange-400/10 border-orange-400/20",
    1: "text-red-400 bg-red-400/10 border-red-400/20",
  };

  return (
    <div className="bg-[#111] border border-white/5 rounded-2xl p-5 flex flex-col gap-4 hover:border-white/10 transition-all duration-300 group">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-amber-400/20 to-amber-600/10 rounded-xl flex-shrink-0 border border-amber-400/15">
            <span className="text-amber-400 font-black text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {review.reviewerName.charAt(0)}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {review.reviewerName}
              </p>
              {review.isVerified && (
                <span className="flex items-center gap-1 bg-emerald-400/10 border border-emerald-400/20 text-emerald-400 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                  <i className="ri-shield-check-line text-xs"></i>
                  مشتري موثّق
                </span>
              )}
            </div>
            <p className="text-gray-600 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>{date}</p>
          </div>
        </div>

        {/* Rating badge */}
        <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-xs font-black flex-shrink-0 ${ratingColors[review.rating] ?? ratingColors[5]}`}>
          <i className="ri-star-fill text-xs"></i>
          <span style={{ fontFamily: "'Cairo', sans-serif" }}>{review.rating}.0</span>
        </div>
      </div>

      {/* Stars row */}
      <StarRating value={review.rating} size="sm" />

      {/* Review text */}
      <p className="text-gray-300 text-sm leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
        {review.reviewText}
      </p>

      {/* Admin reply */}
      {review.adminReply && (
        <div className="border-r-2 border-amber-400/40 pr-4 mr-1 bg-amber-400/3 rounded-l-xl py-3">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-5 h-5 flex items-center justify-center bg-amber-400/20 rounded-full flex-shrink-0">
              <i className="ri-shield-user-fill text-amber-400 text-[10px]"></i>
            </div>
            <p className="text-amber-400 text-xs font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
              رد المتجر
            </p>
            {review.adminReplyAt && (
              <span className="text-gray-600 text-[10px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
                · {new Date(review.adminReplyAt).toLocaleDateString("ar-SA", { month: "short", day: "numeric" })}
              </span>
            )}
          </div>
          <p className="text-gray-400 text-sm leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
            {review.adminReply}
          </p>
        </div>
      )}

      {/* Helpful */}
      <div className="flex items-center justify-between pt-1 border-t border-white/5">
        <p className="text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
          هل كان هذا التقييم مفيداً؟
        </p>
        <div className="flex items-center gap-2">
          {helpful !== 0 && (
            <span className={`text-xs font-bold ${helpful > 0 ? "text-emerald-400" : "text-red-400"}`}
              style={{ fontFamily: "'Cairo', sans-serif" }}>
              {helpful > 0 ? "شكراً!" : "آسفون!"}
            </span>
          )}
          <button
            onClick={() => handleHelpful(1)}
            disabled={voted}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
              voted && helpful > 0
                ? "bg-emerald-400/15 border-emerald-400/30 text-emerald-400"
                : "bg-white/3 border-white/8 text-gray-500 hover:text-emerald-400 hover:border-emerald-400/30 hover:bg-emerald-400/5 disabled:cursor-default"
            }`}
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-thumb-up-line text-xs"></i>
            نعم
          </button>
          <button
            onClick={() => handleHelpful(-1)}
            disabled={voted}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
              voted && helpful < 0
                ? "bg-red-400/15 border-red-400/30 text-red-400"
                : "bg-white/3 border-white/8 text-gray-500 hover:text-red-400 hover:border-red-400/30 hover:bg-red-400/5 disabled:cursor-default"
            }`}
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-thumb-down-line text-xs"></i>
            لا
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Buyer Banner ── */
function BuyerReviewBanner({ productName, onWriteReview }: { productName: string; onWriteReview: () => void }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <div className="relative overflow-hidden bg-gradient-to-l from-amber-400/8 via-amber-400/12 to-amber-500/8 border border-amber-400/30 rounded-2xl p-5 mb-8">
      <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/10 rounded-full blur-2xl pointer-events-none" />
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 relative z-10">
        <div className="w-14 h-14 flex items-center justify-center bg-amber-400/15 border border-amber-400/25 rounded-2xl flex-shrink-0">
          <i className="ri-star-smile-line text-amber-400 text-2xl"></i>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-amber-400 text-black text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}>
              مشتري موثّق
            </span>
          </div>
          <p className="text-white font-black text-base leading-tight" style={{ fontFamily: "'Cairo', sans-serif" }}>
            اشتريت {productName}؟ شاركنا رأيك!
          </p>
          <p className="text-amber-400/70 text-xs mt-1 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
            تقييمك يساعد العملاء الآخرين على اتخاذ قرار الشراء الصحيح
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onWriteReview}
            className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-black px-5 py-2.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-edit-line"></i>
            اكتب تقييماً
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 text-gray-500 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Write Review Form ── */
function WriteReviewForm({
  isLoggedIn, user, productName,
  rating, setRating, reviewText, setReviewText,
  name, setName, phone, setPhone,
  formError, submitError, submitting, submitSuccess,
  onSubmit, onClose,
}: {
  isLoggedIn: boolean;
  user: { name: string; phone: string } | null;
  productName: string;
  rating: number; setRating: (v: number) => void;
  reviewText: string; setReviewText: (v: string) => void;
  name: string; setName: (v: string) => void;
  phone: string; setPhone: (v: string) => void;
  formError: string; submitError: string | null;
  submitting: boolean; submitSuccess: boolean;
  onSubmit: () => void; onClose: () => void;
}) {
  return (
    <div className="bg-[#111] border border-amber-400/25 rounded-2xl p-6 mb-8 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />

      <div className="flex items-center justify-between mb-6">
        <h3 className="text-white font-black text-base flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
          <div className="w-8 h-8 flex items-center justify-center bg-amber-400/15 rounded-lg">
            <i className="ri-edit-line text-amber-400 text-sm"></i>
          </div>
          قيّم {productName}
        </h3>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 text-gray-500 hover:text-white transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>

      {submitSuccess ? (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-emerald-400/15 rounded-full border border-emerald-400/25">
            <i className="ri-checkbox-circle-line text-emerald-400 text-3xl"></i>
          </div>
          <div>
            <p className="text-emerald-400 font-black text-lg" style={{ fontFamily: "'Cairo', sans-serif" }}>شكراً لك!</p>
            <p className="text-gray-400 text-sm mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              تم إرسال تقييمك بنجاح وسيظهر للعملاء الآخرين
            </p>
          </div>
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(s => (
              <i key={s} className={`ri-star-fill text-xl ${s <= rating ? "text-amber-400" : "text-gray-700"}`}></i>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {/* Star picker */}
          <div className="flex flex-col items-center gap-3 py-5 bg-[#0f0f0f] rounded-xl border border-white/5">
            <p className="text-gray-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>كيف تقيّم هذا المنتج؟</p>
            <StarRating value={rating} onChange={setRating} size="lg" />
            {rating > 0 && (
              <p className={`text-sm font-black transition-all ${STAR_COLORS[rating]}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                {STAR_LABELS[rating]}
              </p>
            )}
          </div>

          {/* Name + Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                الاسم <span className="text-amber-400">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                readOnly={isLoggedIn}
                placeholder="اسمك الكامل"
                className={`w-full bg-[#1a1a1a] border ${isLoggedIn ? "border-amber-400/20 text-amber-400/80" : "border-white/10 text-white"} rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-400/40 transition-colors placeholder-gray-600`}
                style={{ fontFamily: "'Cairo', sans-serif" }}
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                رقم الجوال <span className="text-amber-400">*</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                readOnly={isLoggedIn}
                placeholder="05XXXXXXXX"
                className={`w-full bg-[#1a1a1a] border ${isLoggedIn ? "border-amber-400/20 text-amber-400/80" : "border-white/10 text-white"} rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-400/40 transition-colors placeholder-gray-600`}
                style={{ fontFamily: "'Cairo', sans-serif" }}
              />
            </div>
          </div>

          {isLoggedIn && (
            <div className="flex items-center gap-2 bg-emerald-400/5 border border-emerald-400/15 rounded-xl px-4 py-2.5">
              <i className="ri-shield-check-line text-emerald-400 text-sm flex-shrink-0"></i>
              <p className="text-emerald-400/80 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                بياناتك مُعبّأة تلقائياً من حسابك
              </p>
            </div>
          )}

          {/* Review text */}
          <div>
            <label className="text-gray-400 text-xs font-medium block mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              تجربتك مع المنتج <span className="text-amber-400">*</span>
            </label>
            <textarea
              value={reviewText}
              onChange={e => setReviewText(e.target.value.slice(0, 500))}
              placeholder="ما الذي أعجبك في المنتج؟ هل يستحق السعر؟ هل تنصح به؟..."
              rows={4}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-amber-400/40 transition-colors placeholder-gray-600 resize-none"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {reviewText.length < 10 ? (
                  <span className="text-amber-400/60">يرجى كتابة {10 - reviewText.length} حرف على الأقل</span>
                ) : (
                  <span className="text-emerald-400/60">✓ جاهز للإرسال</span>
                )}
              </p>
              <p className="text-gray-600 text-xs">{reviewText.length}/500</p>
            </div>
          </div>

          {/* Errors */}
          {(formError || submitError) && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
              <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0"></i>
              <p className="text-red-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {formError || submitError}
              </p>
            </div>
          )}

          <button
            onClick={onSubmit}
            disabled={submitting || rating === 0}
            className="w-full flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black py-4 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            {submitting ? (
              <><i className="ri-loader-4-line animate-spin"></i> جاري الإرسال...</>
            ) : (
              <><i className="ri-send-plane-fill"></i> إرسال التقييم</>
            )}
          </button>

          <p className="text-gray-600 text-xs text-center" style={{ fontFamily: "'Cairo', sans-serif" }}>
            رقم جوالك يُستخدم فقط للتحقق من أنك مشتري حقيقي ولن يُنشر
          </p>
        </div>
      )}
    </div>
  );
}

/* ── Main Section ── */
export default function ReviewsSection({ productId, productName }: ReviewsSectionProps) {
  const { user, isLoggedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const {
    reviews, loading, stats,
    addReview, submitting, submitError, submitSuccess,
    resetSubmit,
  } = useReviews(productId);

  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [formError, setFormError] = useState("");
  const [filterRating, setFilterRating] = useState(0);
  const [visibleCount, setVisibleCount] = useState(5);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [hasAlreadyReviewed, setHasAlreadyReviewed] = useState(false);
  const [sortBy, setSortBy] = useState<"newest" | "highest" | "lowest" | "verified">("newest");

  // Auto-open form if ?review=1
  useEffect(() => {
    if (searchParams.get("review") === "1") {
      setShowForm(true);
      setTimeout(() => {
        document.getElementById("review-form-anchor")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    }
  }, [searchParams]);

  // Check if logged-in user purchased this product
  useEffect(() => {
    if (!isLoggedIn || !user) return;
    const check = async () => {
      const { data } = await supabase
        .from("orders")
        .select("id, items")
        .eq("phone", user.phone)
        .in("status", ["delivered", "shipped", "confirmed"]);
      if (data && data.length > 0) {
        const purchased = data.some(order => {
          const items = order.items as { id: number }[];
          return Array.isArray(items) && items.some(item => item.id === productId);
        });
        setHasPurchased(purchased);
      }
    };
    check();
  }, [isLoggedIn, user, productId]);

  // Check if already reviewed
  useEffect(() => {
    if (!isLoggedIn || !user) return;
    setHasAlreadyReviewed(reviews.some(r => r.reviewerPhone === user.phone));
  }, [reviews, isLoggedIn, user]);

  // Pre-fill user data
  useEffect(() => {
    if (isLoggedIn && user) { setName(user.name); setPhone(user.phone); }
  }, [isLoggedIn, user]);

  // Close form on success
  useEffect(() => {
    if (submitSuccess) {
      setReviewText("");
      setRating(5);
      setHasAlreadyReviewed(true);
      if (!isLoggedIn) { setName(""); setPhone(""); }
    }
  }, [submitSuccess, isLoggedIn]);

  const handleOpenForm = useCallback(() => {
    setShowForm(true);
    resetSubmit();
    setTimeout(() => {
      document.getElementById("review-form-anchor")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }, [resetSubmit]);

  const handleSubmit = useCallback(async () => {
    setFormError("");
    if (!name.trim() || name.trim().length < 2) { setFormError("يرجى إدخال اسمك"); return; }
    if (!phone.trim() || !/^05\d{8}$/.test(phone.trim())) { setFormError("يرجى إدخال رقم جوال سعودي صحيح (05XXXXXXXX)"); return; }
    if (!reviewText.trim() || reviewText.trim().length < 10) { setFormError("يرجى كتابة تقييم لا يقل عن 10 أحرف"); return; }
    await addReview({ reviewerName: name.trim(), reviewerPhone: phone.trim(), rating, reviewText: reviewText.trim(), customerId: user?.id });
  }, [name, phone, rating, reviewText, user, addReview]);

  // Sort reviews
  const sortedReviews = [...reviews].sort((a, b) => {
    if (sortBy === "highest") return b.rating - a.rating;
    if (sortBy === "lowest") return a.rating - b.rating;
    if (sortBy === "verified") return (b.isVerified ? 1 : 0) - (a.isVerified ? 1 : 0);
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const filtered = filterRating > 0 ? sortedReviews.filter(r => r.rating === filterRating) : sortedReviews;
  const visible = filtered.slice(0, visibleCount);
  const showBuyerBanner = hasPurchased && !hasAlreadyReviewed && !showForm && !submitSuccess;

  return (
    <section className="mt-16 border-t border-white/5 pt-14" dir="rtl" id="reviews">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-amber-400 text-xs tracking-widest uppercase mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
            آراء العملاء
          </p>
          <h2 className="text-2xl font-black text-white flex items-center gap-3" style={{ fontFamily: "'Cairo', sans-serif" }}>
            تقييمات {productName}
            {stats.total > 0 && (
              <span className="flex items-center gap-1.5 bg-amber-400/10 border border-amber-400/20 text-amber-400 text-sm px-3 py-1 rounded-full font-bold">
                <i className="ri-star-fill text-xs"></i>
                {stats.average.toFixed(1)}
                <span className="text-amber-400/60 text-xs font-normal">({stats.total})</span>
              </span>
            )}
          </h2>
        </div>
        {!showForm && !submitSuccess && (
          <button
            onClick={handleOpenForm}
            className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-bold px-5 py-2.5 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-edit-line"></i>
            أضف تقييمك
          </button>
        )}
      </div>

      {/* Buyer Banner */}
      {showBuyerBanner && (
        <BuyerReviewBanner productName={productName} onWriteReview={handleOpenForm} />
      )}

      {/* Stats Overview */}
      {stats.total > 0 && (
        <div className="bg-[#111] border border-white/5 rounded-2xl p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-6 items-center">
            {/* Big score */}
            <div className="flex flex-col items-center gap-2 flex-shrink-0">
              <span className="text-6xl font-black text-amber-400" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {stats.average.toFixed(1)}
              </span>
              <StarRating value={Math.round(stats.average)} size="md" />
              <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                من {stats.total} تقييم
              </p>
              {/* Verified count */}
              {reviews.filter(r => r.isVerified).length > 0 && (
                <span className="flex items-center gap-1 bg-emerald-400/10 border border-emerald-400/20 text-emerald-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  <i className="ri-shield-check-line text-xs"></i>
                  {reviews.filter(r => r.isVerified).length} مشتري موثّق
                </span>
              )}
            </div>

            <div className="hidden sm:block w-px h-24 bg-white/5"></div>
            <div className="sm:hidden w-full h-px bg-white/5"></div>

            {/* Distribution bars */}
            <div className="flex-1 w-full flex flex-col gap-2">
              {([5, 4, 3, 2, 1] as const).map(star => {
                const count = stats.distribution[star];
                const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                return (
                  <button
                    key={star}
                    onClick={() => setFilterRating(filterRating === star ? 0 : star)}
                    className={`flex items-center gap-3 group cursor-pointer rounded-lg px-2 py-1 transition-all ${
                      filterRating === star ? "bg-amber-400/10" : "hover:bg-white/3"
                    }`}
                  >
                    <div className="flex items-center gap-1 w-16 flex-shrink-0">
                      <span className="text-xs text-gray-400" style={{ fontFamily: "'Cairo', sans-serif" }}>{star}</span>
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                    </div>
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-400 rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500 w-8 text-left flex-shrink-0" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Write Review Form */}
      <div id="review-form-anchor">
        {showForm && (
          <WriteReviewForm
            isLoggedIn={isLoggedIn}
            user={user}
            productName={productName}
            rating={rating} setRating={setRating}
            reviewText={reviewText} setReviewText={setReviewText}
            name={name} setName={setName}
            phone={phone} setPhone={setPhone}
            formError={formError}
            submitError={submitError}
            submitting={submitting}
            submitSuccess={submitSuccess}
            onSubmit={handleSubmit}
            onClose={() => { setShowForm(false); resetSubmit(); }}
          />
        )}
      </div>

      {/* Filters + Sort */}
      {stats.total > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
          {/* Rating filter chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setFilterRating(0)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                filterRating === 0 ? "bg-amber-400 text-black" : "bg-[#111] border border-white/8 text-gray-400 hover:text-white"
              }`}
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              الكل ({stats.total})
            </button>
            {([5, 4, 3, 2, 1] as const).map(s => stats.distribution[s] > 0 && (
              <button
                key={s}
                onClick={() => setFilterRating(filterRating === s ? 0 : s)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  filterRating === s ? "bg-amber-400 text-black" : "bg-[#111] border border-white/8 text-gray-400 hover:text-white"
                }`}
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-star-fill text-xs"></i>
                {s} ({stats.distribution[s]})
              </button>
            ))}
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as typeof sortBy)}
            className="bg-[#111] border border-white/8 text-gray-400 text-xs rounded-xl px-3 py-2 outline-none cursor-pointer"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <option value="newest">الأحدث أولاً</option>
            <option value="highest">الأعلى تقييماً</option>
            <option value="lowest">الأدنى تقييماً</option>
            <option value="verified">المشترون الموثّقون</option>
          </select>
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="w-10 h-10 border-2 border-white/10 border-t-amber-400 rounded-full animate-spin"></div>
          <p className="text-gray-500 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>جاري تحميل التقييمات...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 gap-4 bg-[#111] border border-white/5 rounded-2xl">
          <div className="w-16 h-16 flex items-center justify-center bg-amber-400/10 rounded-2xl">
            <i className="ri-star-line text-amber-400 text-3xl"></i>
          </div>
          <div className="text-center">
            <p className="text-white font-bold text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {filterRating > 0 ? `لا توجد تقييمات بـ ${filterRating} نجوم` : "كن أول من يقيّم هذا المنتج!"}
            </p>
            <p className="text-gray-500 text-sm mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {filterRating > 0 ? "جرّب فلتراً آخر" : "تقييمك يساعد العملاء الآخرين على اتخاذ القرار الصحيح"}
            </p>
          </div>
          {filterRating === 0 && !showForm && (
            <button
              onClick={handleOpenForm}
              className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-bold px-5 py-2.5 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-edit-line"></i>
              اكتب أول تقييم
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {visible.map(review => (
            <ReviewCard key={review.id} review={review} />
          ))}

          {filtered.length > visibleCount && (
            <button
              onClick={() => setVisibleCount(v => v + 5)}
              className="w-full flex items-center justify-center gap-2 border border-white/10 text-gray-400 hover:text-amber-400 hover:border-amber-400/30 py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-arrow-down-line"></i>
              عرض المزيد ({filtered.length - visibleCount} تقييم)
            </button>
          )}
        </div>
      )}
    </section>
  );
}
